import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AlertTriangle, ChevronDown, FileText } from 'lucide-react'
import {
  KnowledgeAskCitation,
  KnowledgeDocumentListItem,
} from '@digital-twins/shared-types'
import { Heading } from '@/components/ui/Heading'
import { Text } from '@/components/ui/Text'
import { cn } from '@/lib/utils'
import {
  documentCoverUrl,
  documentTypeLabel,
  facetValue,
} from '../KnowledgeHubPage/libraryHelpers'
import {
  citationSocialPost,
  SocialCitationPostMeta,
} from '../KnowledgeHubPage/social/SocialCitationPostMeta'
import {
  CITATION_VERIFICATION,
  citationVerification,
} from './citationVerification'
import { CITATION_MARKER_CHIP_CLASS } from './ThreadCitationMarker'
import {
  CitationDocumentGroup,
  isSourceFairlyOld,
} from './groupCitationsByDocument'

interface ThreadSourceCardProps {
  group: CitationDocumentGroup
  document?: KnowledgeDocumentListItem | null
  title: string
  onOpenCitation: (index: number, citation: KnowledgeAskCitation) => void
}

export function ThreadSourceCard(props: ThreadSourceCardProps) {
  const { group, document, title, onOpenCitation } = props

  const { t } = useTranslation('app')

  const [expanded, setExpanded] = useState(false)

  const coverSrc = document ? documentCoverUrl(document) : null
  const year = facetValue(document?.facets, 'year')
  const market = facetValue(document?.facets, 'market')
  const typeLabel = document ? documentTypeLabel(document) : null
  const metaParts = [year, market, typeLabel].filter(Boolean)
  const fairlyOld = isSourceFairlyOld(year)
  const referenceCount = group.citations.length
  const first = group.citations[0]

  const openFirst = () => {
    if (!first) {
      return
    }
    onOpenCitation(first.index, first.citation)
  }

  return (
    <article
      className="space-y-3 rounded-xl bg-muted p-4"
      data-testid="thread-source-card"
      data-document-key={group.key}
    >
      <button
        type="button"
        className="flex w-full items-start gap-3 text-left"
        data-testid="thread-source-card-open"
        onClick={openFirst}
      >
        <div className="h-16 w-12 shrink-0 overflow-hidden rounded-md bg-background">
          {coverSrc ? (
            <img
              src={coverSrc}
              alt=""
              className="h-full w-full object-cover object-top"
              data-testid="thread-source-card-thumb"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-muted-foreground">
              <FileText className="h-5 w-5" aria-hidden />
            </div>
          )}
        </div>
        <div className="min-w-0 flex-1 space-y-1">
          <Heading level={3} size="sm" weight="medium" className="line-clamp-2">
            {title}
          </Heading>
          <div className="flex flex-wrap items-center gap-1.5 text-muted-foreground">
            <FileText className="h-3 w-3 shrink-0" aria-hidden />
            {metaParts.length > 0 ? (
              <Text as="span" size="xs" color="muted">
                {metaParts.join(' · ')}
              </Text>
            ) : (
              <Text as="span" size="xs" color="muted">
                {t('library.type.document')}
              </Text>
            )}
          </div>
        </div>
      </button>

      {fairlyOld ? (
        <div
          className="flex items-center gap-1.5"
          data-testid="thread-source-card-old"
        >
          <AlertTriangle
            className="h-3.5 w-3.5 shrink-0 text-chart-warning"
            aria-hidden
          />
          <Text as="span" size="xs" color="muted">
            {t('threads.detail.thisSourceIsFairlyOld')}
          </Text>
        </div>
      ) : null}

      <button
        type="button"
        className="flex w-full items-center justify-between rounded-lg bg-background px-3 py-2 text-xs"
        data-testid="thread-source-card-toggle"
        aria-expanded={expanded}
        onClick={() => setExpanded((current) => !current)}
      >
        <Text as="span" size="xs">
          {t(
            expanded
              ? 'threads.detail.hideReferences'
              : 'threads.detail.showReferences',
            { count: referenceCount },
          )}
        </Text>
        <ChevronDown
          className={cn(
            'h-3.5 w-3.5 text-muted-foreground transition-transform',
            expanded ? 'rotate-180' : '',
          )}
          aria-hidden
        />
      </button>

      {expanded ? (
        <ul className="space-y-2" data-testid="thread-source-card-references">
          {group.citations.map((item) => {
            const verification = citationVerification(item.citation)
            const socialPost = citationSocialPost(item.citation)
            const pageLabel =
              !socialPost && !item.citation.pageUnknown && item.citation.pageNumber != null
                ? `p.${item.citation.pageNumber}`
                : null
            return (
              <li key={`${group.key}-${item.index}`}>
                <button
                  type="button"
                  className="flex w-full items-start gap-2 rounded-lg px-1 py-1 text-left hover:bg-background/80"
                  data-testid={`thread-source-card-reference-${item.index}`}
                  onClick={() => onOpenCitation(item.index, item.citation)}
                >
                  <span
                    className={cn(
                      'mt-0.5 shrink-0',
                      CITATION_MARKER_CHIP_CLASS,
                      CITATION_VERIFICATION[verification].markerClassName,
                    )}
                  >
                    {item.index}
                  </span>
                  {socialPost?.thumbnailUrl ? (
                    <img
                      src={socialPost.thumbnailUrl}
                      alt=""
                      referrerPolicy="no-referrer"
                      onError={(event) => {
                        event.currentTarget.style.display = 'none'
                      }}
                      className="h-12 w-12 shrink-0 rounded-md object-cover"
                    />
                  ) : null}
                  <span className="min-w-0 flex-1">
                    <Text as="span" size="xs" className="line-clamp-3 leading-snug">
                      {socialPost?.text ?? (item.citation.snippet || title)}
                    </Text>
                    {pageLabel ? (
                      <Text as="span" size="xs" color="muted" className="mt-0.5 block">
                        {pageLabel}
                      </Text>
                    ) : null}
                  </span>
                </button>
                {socialPost ? (
                  <SocialCitationPostMeta
                    post={socialPost}
                    className="pl-8"
                    testId={`thread-source-card-post-${item.index}`}
                  />
                ) : null}
              </li>
            )
          })}
        </ul>
      ) : null}
    </article>
  )
}
